<template>
  <NewHouseLayout :breadcrumbs="[{ name: '选择房屋' }]">
    <div class="nh-page">
    <div class="nh-card">
      <h3 class="nh-section-title">选择房屋</h3>

      <ul v-if="houseList.length" class="nh-house-list">
        <li
          v-for="h in houseList"
          :key="h.id"
          :class="['nh-house-item', { active: selected === h.id }]"
          @click="selected = h.id"
        >
          <div :class="['nh-radio', { active: selected === h.id }]">
            <span v-if="selected === h.id" class="radio-tick">✓</span>
          </div>
          <div class="nh-house-info">
            <div class="nh-house-name">{{ h.projectName }}</div>
            <div class="nh-house-meta">合同编号：{{ h.contractNo }}</div>
          </div>
          <div class="nh-house-detail">
            <div class="nh-house-room">-{{ h.roomNo }}</div>
            <div class="nh-house-area">面积：{{ h.area }} 平方米</div>
          </div>
        </li>
      </ul>
      <el-empty
        v-else
        class="nh-empty"
        description="暂无可办理的房屋信息"
      />
    </div>

    <div class="nh-footer">
      <el-button size="large" class="nh-btn nh-btn-prev" @click="handlePrev">
        上一步
      </el-button>
      <el-button
        type="danger"
        size="large"
        class="nh-btn nh-btn-next"
        :disabled="!selected"
        @click="handleNext"
      >
        下一步
      </el-button>
    </div>
  </div>
  </NewHouseLayout>
</template>

<script setup>
import NewHouseLayout from './layout.vue';
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getMyContractListRe, createBusinessRe } from '@/api/newHouse';
import { decodeData } from '@/utils/decodeData';
import { ElMessage } from 'element-plus';

const route = useRoute();
const router = useRouter();

const houseList = ref([]);

// 加载个人合同列表
const fetchContractList = async () => {
  try {
    // 从路由 query 拿选中的业务 id 数组
    const biz = route.query.biz ? String(route.query.biz).split(',') : [];
    const res = await getMyContractListRe({
      htbh: '',
      dbsxIds: biz,
    });
    const decoded = decodeData(res?.data);
    const list = Array.isArray(decoded) ? decoded : (decoded?.data || []);
    houseList.value = list.map((it, i) => ({
      id: it.id || it.htbh || String(i),
      projectName: it.xmmc,
      contractNo: it.htbh,
      roomNo: it.fh,
      area: it.jzmj,
    }));
    if (houseList.value.length > 0) {
      selected.value = houseList.value[0].id;
    }
  } catch (e) {
    houseList.value = [];
  }
};

onMounted(() => {
  fetchContractList();
});

const selected = ref(1);

const handlePrev = () => {
  router.push({
    name: 'newHouseSelectBusiness',
    query: { biz: route.query.biz },
  });
};

const handleNext = async () => {
  const house = houseList.value.find((h) => h.id === selected.value);
  if (!house) {
    ElMessage.warning('请先选择房屋');
    return;
  }
  const htbh = house.contractNo || '';
  const biz = String(route.query.biz || '').split(',').filter(Boolean);

  // 选完房后调用 ywcj 创建业务，拿到 ywId
  try {
    const res = await createBusinessRe({ htbh, dbsxIds: biz });
    const decoded = decodeData(res?.data);
    const ywId = decoded?.data || decoded;
    if (!ywId) {
      ElMessage.error('创建业务失败：未返回业务 id');
      return;
    }
    router.push({
      name: 'newHouseBusinessDetail',
      query: {
        ywId,
        biz: route.query.biz,
        houseId: house.id,
        htbh,
      },
    });
  } catch (e) {
    ElMessage.error('创建业务失败');
  }
};
</script>

<style lang="scss" scoped>
.nh-page {
  background: #ffffff;
  padding: 16px 0 24px;
  box-sizing: border-box;
}

.nh-card {
  max-width: 960px;
  margin: 0 auto;
}

.nh-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0d9d9;
  text-align: left;
}

.nh-house-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nh-house-item {
  display: flex;
  align-items: center;
  border: 1px solid #f0d9d9;
  border-radius: 6px;
  padding: 20px 24px;
  margin-bottom: 16px;
  cursor: pointer;
  background: #ffffff;
  transition: border-color 0.2s;

  &.active {
    border-color: #bd1a2d;
  }
}

.nh-radio {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: #ffffff;
  font-size: 14px;

  &.active {
    background: #bd1a2d;
    border-color: #bd1a2d;
  }
}

.radio-tick {
  font-size: 14px;
  line-height: 1;
  color: #ffffff;
}

.nh-house-info {
  flex: 0 0 320px;
  text-align: left;
}

.nh-house-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
}

.nh-house-meta {
  font-size: 13px;
  color: #888;
}

.nh-house-detail {
  flex: 1;
  text-align: left;
  font-size: 14px;
  color: #2c3e50;
}

.nh-house-room {
  margin-bottom: 6px;
}

.nh-house-area {
  color: #888;
  font-size: 13px;
}

.nh-footer {
  max-width: 1200px;
  margin: 24px auto 0;
  background: #ffffff;
  padding: 16px;
  display: flex;
  justify-content: center;
  gap: 24px;
  border-radius: 6px;
}

.nh-btn {
  width: 200px;
  height: 44px;
  font-size: 16px;
}

.nh-btn-prev {
  background: #fdecec;
  color: #bd1a2d;
  border-color: #fdecec;
}

.nh-btn-next {
  background: #bd1a2d;
  border-color: #bd1a2d;
}
</style>
